<template>
  <div class="access-editor">
    <aside class="module-nav" aria-label="Permission modules">
      <app-input
        v-model="search"
        design="new"
        type="search"
        placeholder="Find module or action"
        aria-label="Find module or action"
      />
      <section v-for="section in sections" :key="section.name" class="module-section">
        <button
          type="button"
          class="section-label"
          :aria-expanded="sectionOn(section.name) || Boolean(search)"
          @click="toggleSection(section.name)"
        >
          <span class="section-chevron" :class="{ 'section-chevron--open': sectionOn(section.name) || search }" aria-hidden="true">›</span>
          <strong>{{ section.name }}</strong>
          <span class="section-count">{{ section.modules.filter(enabled).length }}/{{ section.modules.length }}</span>
        </button>
        <div v-if="sectionOn(section.name) || search" class="section-modules">
          <div
            v-for="module in section.modules"
            :key="module.key"
            class="module-item"
            :class="{ 'module-item--selected': current && current.key === module.key }"
          >
            <button
              type="button"
              class="module-link"
              :aria-current="current && current.key === module.key ? 'true' : null"
              @click="currentKey = module.key"
            >{{ module.label }}</button>
            <app-switcher
              design="new"
              :value="enabled(module)"
              :aria-label="`${enabled(module) ? 'Disable' : 'Enable'} ${module.label}`"
              @change="toggleModule(module, $event)"
            />
          </div>
        </div>
      </section>
      <p v-if="!sections.length" class="muted">No matching modules.</p>
    </aside>
    <section v-if="current" class="action-panel" :aria-label="`${current.label} permissions`">
      <header class="action-heading">
        <div>
          <h3>{{ current.label }}</h3>
          <p class="muted">
            {{
              enabled(current)
                ? 'Viewing is included. Choose what else this role can do.'
                : 'Enable this module to allow viewing and choose actions.'
            }}
          </p>
        </div>
        <app-switcher
          design="new"
          :value="enabled(current)"
          :aria-label="`${enabled(current) ? 'Disable' : 'Enable'} ${current.label}`"
          @change="toggleModule(current, $event)"
        />
      </header>
      <template v-if="enabled(current)">
        <div class="view-note">
          <span class="action-name"><span aria-hidden="true">✓</span> View lists and details</span>
          <span class="action-description">Included when the module is enabled.</span>
        </div>
        <div v-for="permission in actions" :key="permission.key" class="action-row">
          <app-switcher
            design="new"
            :value="value.includes(permission.key)"
            :aria-label="`${value.includes(permission.key) ? 'Disable' : 'Enable'} ${permission.label}`"
            @change="toggleAction(permission, $event)"
          />
          <span class="action-name" :title="permission.label">{{ permission.label }}</span>
          <span class="action-description" :title="descriptionFor(permission)">{{ descriptionFor(permission) }}</span>
        </div>
        <p v-if="!actions.length" class="muted">
          {{
            search
              ? 'No matching actions. Clear the search to see all actions.'
              : 'This module only includes viewing.'
          }}
        </p>
      </template>
      <div v-else class="module-empty">
        <p>No access to {{ current.label.toLowerCase() }}.</p>
        <p class="muted">Enable the module above to configure its actions.</p>
      </div>
      <p v-if="notice" class="dependency-note" role="status">{{ notice }}</p>
    </section>
    <section v-else class="module-empty">
      <h3>Choose a module</h3>
      <p class="muted">Expand a section on the left, then select a module.</p>
    </section>
  </div>
</template>

<script>
import { definitions, normalizeSelection, removeSelection } from '@/utils/permissionSelection'
export default {
  props: { modules: { type: Array, required: true }, value: { type: Array, required: true } },
  data: () => ({ search: '', currentKey: '', opened: [], notice: '' }),
  created() {
    this.opened = [...new Set(this.modules.filter(this.enabled).map((module) => module.section))]
  },
  computed: {
    sections() {
      const query = (this.search || '').toLowerCase()
      const grouped = []
      this.modules
        .filter((m) => `${m.label} ${m.section} ${m.permissions.map((p) => p.label).join(' ')}`
          .toLowerCase()
          .includes(query)
        )
        .forEach((module) => {
          let section = grouped.find((s) => s.name === module.section)
          if (!section) {
            section = { name: module.section, modules: [] }
            grouped.push(section)
          }
          section.modules.push(module)
        })
      return grouped
    },
    available() {
      return this.sections
        .filter((s) => this.sectionOn(s.name) || this.search)
        .reduce((all, s) => all.concat(s.modules), [])
    },
    current() {
      return (
        this.available.find((m) => m.key === this.currentKey) ||
        this.available.find(this.enabled) ||
        this.available[0]
      )
    },
    actions() {
      const query = (this.search || '').toLowerCase()
      const moduleMatch = `${this.current.label} ${this.current.section}`.toLowerCase().includes(query)
      return this.current.permissions.filter(
        (p) => p.action !== 'list' && (moduleMatch || p.label.toLowerCase().includes(query))
      )
    }
  },
  methods: {
    enabled(module) {
      return module.permissions.some((p) => this.value.includes(p.key))
    },
    sectionOn(name) {
      return this.opened.includes(name)
    },
    labelFor(key) {
      return definitions(this.modules).find((p) => p.key === key)?.label || key
    },
    descriptionFor(p) {
      const required = (p.requires || []).filter((key) => !key.endsWith('.list'))
      return [p.description, required.length ? `Also includes: ${required.map(this.labelFor).join(', ')}.` : '']
        .filter(Boolean).join(' ') || '—'
    },
    add(keys) {
      const requested = [...this.value, ...keys]
      const result = normalizeSelection(requested, this.modules)
      const added = result.filter((key) => !requested.includes(key) && !key.endsWith('.list'))
      this.notice = added.length ? `Also enabled: ${added.map(this.labelFor).join(', ')}. Required by your selection.` : ''
      this.$emit('input', result)
    },
    remove(keys, after = () => {}) {
      const result = removeSelection(this.value, keys, this.modules)
      const removed = this.value.filter((key) => !result.includes(key))
      const apply = () => {
        this.$emit('input', result)
        this.notice = ''
        after()
      }
      if (removed.length > 1) this.$emit('confirm-remove', { removed, apply })
      else apply()
    },
    toggleSection(name) {
      if (this.sectionOn(name)) this.opened = this.opened.filter((section) => section !== name)
      else this.opened = [...this.opened, name]
    },
    toggleModule(module, on) {
      if (on) this.add([`${module.key}.list`])
      else this.remove(module.permissions.map((p) => p.key))
    },
    toggleAction(p, on) {
      if (on) this.add([p.key])
      else this.remove([p.key])
    }
  }
}
</script>

<style scoped lang="scss">
.access-editor {
  display: grid;
  grid-template-columns: minmax(280px, 320px) minmax(0, 1fr);
  gap: $ui-space-24;
}
.module-nav {
  border-right: 1px solid $ui-line;
  padding-right: $ui-space-16;
}
.module-section {
  margin-top: $ui-space-8;
}
.section-label,
.action-row,
.action-heading,
.view-note {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $ui-space-12;
}
.section-label {
  width: 100%;
  min-height: 36px;
  padding: $ui-space-4 $ui-space-8;
  border-radius: $ui-control-radius;
  text-align: left;
  color: $ui-ink;
  font: inherit;
}
.section-label:hover {
  background: $ui-surface-soft;
}
.section-label strong {
  flex: 1;
  font-weight: 700;
}
.section-chevron {
  color: $ui-muted;
  font-size: 22px;
  line-height: 1;
  transform: rotate(0);
  transition: transform .2s;
}
.section-chevron--open {
  transform: rotate(90deg);
}
.section-count {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}
.section-modules {
  margin: $ui-space-4 0 $ui-space-8 $ui-space-16;
  padding-left: $ui-space-12;
  border-left: 2px solid $ui-line;
}
.module-item {
  display: flex;
  align-items: center;
  gap: $ui-space-8;
  min-height: 36px;
  padding: $ui-space-4 $ui-space-8;
  border-radius: $ui-control-radius;
}
.module-item:hover {
  background: $ui-surface-soft;
}
.module-item--selected {
  background: $ui-blue-soft;
}
.module-item--selected .module-link {
  color: $ui-blue;
  font-weight: 600;
}
.module-link {
  flex: 1;
  min-width: 0;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: $ui-text;
  font: inherit;
}
.module-item :deep(.switcher__view) {
  transform: scale(.83);
  transform-origin: right center;
}
.action-panel {
  min-width: 0;
  padding-left: $ui-space-8;
}
h3 {
  font: 600 $ui-font-size-section-title $ui-font-heading;
  color: $ui-ink;
  margin: 0 0 $ui-space-8;
}
.muted {
  color: $ui-muted;
  line-height: 1.45;
}
.action-heading {
  align-items: flex-start;
  flex-wrap: wrap;
  padding-bottom: $ui-space-16;
  border-bottom: 1px solid $ui-line;
}
.action-heading > div {
  flex: 1 1 260px;
}
.view-note {
  display: grid;
  grid-template-columns: minmax(160px, 32%) minmax(0, 1fr);
  color: $ui-green;
  min-height: 44px;
  padding-left: 52px;
}
.action-name {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.action-description {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: $ui-muted;
  font-size: inherit;
}
.action-row {
  display: grid;
  grid-template-columns: 40px minmax(160px, 32%) minmax(0, 1fr);
  min-height: 44px;
  justify-content: flex-start;
  border-top: 1px solid $ui-line;
}
button:focus-visible {
  outline: 2px solid $ui-blue;
  outline-offset: 2px;
}
.module-empty {
  padding: $ui-space-32 $ui-space-16;
  text-align: center;
  line-height: 1.6;
}
.dependency-note {
  margin-top: $ui-space-16;
  padding: $ui-space-12;
  background: $ui-blue-soft;
  border-radius: $ui-control-radius;
}
@media (max-width: 1200px) {
  .access-editor {
    grid-template-columns: 1fr;
  }
  .module-nav {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 $ui-space-16;
    border-right: 0;
    border-bottom: 1px solid $ui-line;
    padding: 0 0 $ui-space-16;
  }
  .module-nav > :first-child,
  .module-nav > .muted {
    grid-column: 1 / -1;
  }
  .action-panel {
    padding-left: 0;
  }
}
@media (max-width: 600px) {
  .module-nav { grid-template-columns: 1fr; }
  .action-row { grid-template-columns: 40px minmax(0, 1fr); }
  .action-row .action-description { grid-column: 2; white-space: normal; padding-bottom: $ui-space-8; }
  .view-note { grid-template-columns: 1fr; padding-left: 52px; }
}
</style>
